import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

import {
  BannerDescriptionWrapper,
  BannerTextWrapper,
  StyledButton,
  StyledReadMoreButton,
} from '@/components/Banner/Banner.styled'
import { LocaleText } from '@/components/LocaleText'
import { RichText } from '@/components/RichText'
import { useTranslations } from '@/hooks/useTranslations'

import type * as React from 'react'
import type { UnpackedSanityContentBanner } from '@/lib/queries/sanityQueryStrings'

interface BannerProps {
  data: UnpackedSanityContentBanner
}

export const BannerText: React.FC<BannerProps> = ({ data }) => {
  const { translate, language } = useTranslations()

  const openLinkOnClick = (link: string, openInNewTab: boolean, e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    window.open(link, openInNewTab ? '_blank' : '_self')
  }

  return (
    <BannerTextWrapper
      gap={2}
      sx={(theme) => ({
        right: data.contentPlacement === 'right' ? theme.customDimensions.heroBannerTextSpace : 'auto',
        left: data.contentPlacement === 'left' ? theme.customDimensions.heroBannerTextSpace : 'auto',
      })}
    >
      {!!data.headerText && (
        <LocaleText
          {...data.headerText}
          typographyProps={{
            variant: 'h1',
            component: 'h2',
            fontSize: { xs: '32px', lg: '40px' },
            lineHeight: { xs: '38px', lg: '48px' },
          }}
        />
      )}
      <BannerDescriptionWrapper className="BannerDescriptionWrapper">
        <RichText Text={data.description} _type="richText" />
      </BannerDescriptionWrapper>

      {data.button?.url && (
        <StyledButton
          variant="contained"
          onClick={(e) => openLinkOnClick(data.button.url ?? '', data.button?.openInNewTab ?? false, e)}
        >
          {data.button?.externalName && data.button?.externalName?.[language] ? (
            <LocaleText {...data.button?.externalName} />
          ) : (
            translate('banner.button.label.read_more')
          )}
        </StyledButton>
      )}
      {data.readMoreLink?.url && (
        <StyledReadMoreButton
          onClick={(e) => openLinkOnClick(data.readMoreLink.url ?? '', data.readMoreLink?.openInNewTab ?? false, e)}
          variant="link"
          endIcon={<ArrowForwardIcon />}
        >
          {data.readMoreLink?.externalName && data.readMoreLink?.externalName?.[language] ? (
            <LocaleText {...data.readMoreLink?.externalName} />
          ) : (
            translate('banner.button.label.read_more')
          )}
        </StyledReadMoreButton>
      )}
    </BannerTextWrapper>
  )
}
